import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** Animate the actual grid height, including incomplete rows, without max-height guesses. */
export default function useFeatureReveal() {
  const [showMore, setShowMore] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const featureRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const previousHeight = useRef<number | null>(null);

  const handleShowMoreClick = () => {
    if (revealing) return;
    previousHeight.current = featureRef.current?.getBoundingClientRect().height ?? null;
    setShowMore(value => !value);
  };

  useBrowserLayoutEffect(() => {
    const grid = featureRef.current;
    const from = previousHeight.current;
    if (!grid || from === null) return;
    previousHeight.current = null;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const additions = [...grid.querySelectorAll<HTMLElement>('[data-additional-feature="true"]')];
    const target = showMore ? additions[0] : toggleRef.current;
    const orient = () => {
      if (!target) return;
      const rect = target.getBoundingClientRect();
      // Leave a visible reference to the preceding row and room for the fixed header.
      if (rect.top < 120 || rect.bottom > window.innerHeight - 80) {
        window.scrollTo({ top: Math.max(0, window.scrollY + rect.top - 140), behavior: reduced ? 'instant' : 'smooth' });
      }
    };
    if (reduced) { orient(); return; }

    setRevealing(true);
    const animation = grid.animate([
      { height: `${from}px`, overflow: 'clip' },
      { height: `${grid.getBoundingClientRect().height}px`, overflow: 'clip' },
    ], { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
    const cards = showMore ? additions.map((card, index) => card.animate([
      { opacity: 0, transform: 'translateY(12px)' },
      { opacity: 1, transform: 'translateY(0)' },
    ], { duration: 320, delay: Math.min(index * 35, 140), fill: 'backwards', easing: 'ease-out' })) : [];
    animation.onfinish = () => { setRevealing(false); orient(); };
    return () => { animation.cancel(); cards.forEach(card => card.cancel()); };
  }, [showMore]);

  return { showMore, revealing, featureRef, toggleRef, handleShowMoreClick };
}
